/* ============================================================
   sc-nadia.js
   Scripted Nadia chat with per-page context.
   Plus page-level interactions: drawer, accordion, config selectors.

   The scripts are deterministic but feel conversational.
   Same widget surface that the real Vercel proxy will hook into.
   ============================================================ */

(function() {
  'use strict';

  /* ------------------- NADIA SCRIPTS PER CONTEXT ------------------- */
  /* Each script is { opener, fallback, rules: [{ match: regex|fn, reply: str|fn }] } */

  var scripts = {
    'home': {
      opener: 'Hello, I am Nadia. I can help you find the right sofa for your room, or just answer questions if you are browsing. What brings you in today?',
      rules: [
        {
          match: /sofa|coco|barton|isobel|henrietta|felix/i,
          reply: 'Lovely. The Coco is our flagship and the most asked-about. Generous, hand-built, individual coil sprung, in any size and any fabric. The Barton is the deepest. The Isobel is slimmer and more affordable. Tell me a little about the room and the people who use it, and I can probably narrow it down for you in two minutes.'
        },
        {
          match: /quick|fast|soon|stock|hurry|delivery/i,
          reply: 'For quick delivery, our stock-base programme has sofas ready in three to six weeks rather than the usual eight. Cool Coconut and Snow Drop are our most-stocked plain fabrics. Would you like me to show you what is ready right now?'
        },
        {
          match: /bed|linen|sheet|duvet|pillow/i,
          reply: 'The Scalloped bed linen comes in eight colours, from £73.50 for a single duvet cover. Hand-finished scalloped edges, Sophie\'s own design. Pop over to the bed linen world and have a look, or tell me a colour and I can describe how it sits in a room.'
        },
        {
          match: /price|cost|budget|cheap|afford/i,
          reply: 'Prices start at around £3,895 for a Felix 2-seater and rise from there. The Coco 2.5-seater is from £5,895 in the standard fabric range. If your budget needs to come below that, the Cocobella at John Lewis is the same Sophie design with 0% finance available. May I ask what range you are working with?'
        },
        {
          match: /natural|chemical|fr|fire|retardant|sustainab/i,
          reply: 'A real point of difference for us. Every UK sofa must meet fire safety regulations and ours do. On Coco and Barton, our lowest-chemical models, we are developing a build option that uses a wool fire barrier in place of a chemically treated interliner. Sophie has written a letter explaining how it works if you would like to read it.'
        },
        {
          match: /sample|swatch|fabric/i,
          reply: 'Free samples for engaged visitors, up to five per request. They arrive within a week. Tell me your name, email and shipping address and I will send them out for you. Which fabrics caught your eye?'
        },
        {
          match: /trade|designer|architect/i,
          reply: 'Welcome. The trade portal is the right home for you. Trade pricing across the range, the quote builder for client work, and tear sheets and lookbooks you can present. Would you like me to point you there, or do you already have an account?'
        }
      ],
      fallback: 'Tell me a little more about what you are working on. The room, the people who use it, the look you have in mind, anything that helps me understand. The more I know, the better I can help.'
    },

    'product': {
      opener: 'You are looking at the Coco 2.5. Want me to talk you through whether it suits your room, or shall I help you choose between fabrics?',
      rules: [
        {
          match: /fabric|cool coconut|snow drop|wildwood|stripe|velvet|linen/i,
          reply: 'The Coco comes in any of our fabrics. Cool Coconut and Snow Drop are our plain cotton-linens, both machine-washable at 30 degrees and family-friendly. Stripes (Dandelion, Petunia, Coastal Blue) are also organic-yarn cotton-linen, washable. Wildwood Duck Egg is a viscose-linen blend with a floral print, dry-clean only. Florals and prints are generally dry-clean only. What direction are you drawn to?'
        },
        {
          match: /room|fit|size|dimension|measure/i,
          reply: 'The 2.5-seater is 195cm wide and 105cm deep, which suits most living rooms with a wall of around 4 metres. If you have measurements I can sense-check it for you. Otherwise the workshop can size it to anything you need, the bespoke door is on the page.'
        },
        {
          match: /delivery|lead|wait|time|when|how long/i,
          reply: 'Around 8 weeks from order, often sooner. We hold stock of the most popular bases in Cool Coconut, Snow Drop and a few stripes, which means three to six weeks if one matches. Would you like me to check what is ready now in this size?'
        },
        {
          match: /wash|clean|care|maintain|spill|stain/i,
          reply: 'Depends on the fabric. The plain cotton-linens (Cool Coconut, Snow Drop) and the stripes are machine-washable at 30 degrees, which is what most families end up choosing. Florals and prints, including Wildwood Duck Egg, are dry-clean only. The cushion covers come off and go straight in the machine. We use no chemical flame retardants either, which matters if anyone in the household has chemical sensitivity.'
        },
        {
          match: /bespoke|custom|own|specific|specify/i,
          reply: 'Bespoke is where the workshop gets really interesting. Any size, any fabric including your own (we call it COM, customer\'s own material), any cushion construction, any leg. Tell me what you have in mind and I will brief the team to call you about whether it is possible and how much.'
        },
        {
          match: /price|cost|how much|budget/i,
          reply: 'The 2.5-seater is from £5,895 in our standard fabric range. Stripes and plain cotton-linens are at that price. Designers Guild fabrics like Brera Lino or Varese are a step up, around 10 to 15 per cent more. Bespoke sizes price up from there. Would a price for a specific fabric help?'
        },
        {
          match: /split|frame|narrow|door|stairs/i,
          reply: 'Yes. The Coco 2.5-seater is available with a split-frame option, which means we deliver in two halves and assemble in the room. Useful for narrow doorways and tight stairs. (The Coco sofa bed is the one exception, it does not split.) If you can tell me the smallest opening the sofa needs to fit through, I can confirm whether you need it.'
        },
        {
          match: /sample|swatch/i,
          reply: 'Of course. Up to four free samples, posted to you within a week. Tell me your name, email and address, and which fabrics you would like.'
        }
      ],
      fallback: 'Anything else you would like to know about this Coco? I can talk about fabric, construction, the bespoke service, the lead time, or how it lives in a real room.'
    },

    'in-stock': {
      opener: 'I can see you are looking at quick-ship options. Want me to walk you through what is actually available right now in the size and fabric you have in mind?',
      rules: [
        {
          match: /coco/i,
          reply: 'Currently we have the Coco 2.5 in Cool Coconut ready in three weeks, the Coco 3 in Dandelion Stripe ready in five weeks, the Coco loveseat in Snow Drop ready in three weeks, and a Coco sofa bed in Cool Coconut ready in six weeks. Which size and fabric would suit?'
        },
        {
          match: /three week|3 week|fastest|soonest/i,
          reply: 'The fastest right now is three weeks. We have the Coco 2.5-seater in Cool Coconut (two available) and the Coco loveseat in Snow Drop (two available) at that lead time. Both are plain cotton-linen, machine-washable at 30 degrees. Would either work?'
        },
        {
          match: /family|child|kid|dog|cat|pet/i,
          reply: 'For a family-friendly stock pick, I would point you at Cool Coconut or Snow Drop on the Coco. Plain cotton-linen, washable at 30 degrees, knife-edge cushions that hold their shape, and we use no chemical flame retardants. Cotton velvets are lovely but they attract pet hair, so I would steer clear of those if there is a shedding dog in the picture.'
        },
        {
          match: /reserve|hold|book|order/i,
          reply: 'Of course. Once you tap through to one of the cards, the stock is held for 24 hours while you complete the order. If you need longer, give me your phone number and Adam can call to extend it.'
        },
        {
          match: /delivery|when|date/i,
          reply: 'The lead time on each card is from order. So if you order today, three weeks means delivery the third week from now. Our delivery team books a slot once the sofa is ready and confirms a date. Where are we delivering to?'
        }
      ],
      fallback: 'Tell me what range, what size, what fabric direction (plain or stripe or floral), and how soon you need it, and I can match you to what is actually available.'
    },

    'bed-linen': {
      opener: 'Hello. The Scalloped Collection in eight colours, sent from our UK warehouse. UK delivery only at the moment, US to follow. Ask me about sizes, colours, care, or how the linen pairs with the rest of the house.',
      rules: [
        {
          match: /size|king|emperor|double|single/i,
          reply: 'The Scalloped collection comes in Single, Double, King, Super King and Emperor. Not every colour is available in every size, the catalogue shows what we hold for each colour. Single duvet covers from £73.50, full sets from £121.55 (Single) up to £219.30 (Emperor). What size bed are we dressing?'
        },
        {
          match: /colour|color|pink|navy|blue|green|red|coral|orange/i,
          reply: 'Eight colours: Navy, White, Pink, Red, Blue, Green, Coral and Orange. Coral and Orange are the more limited offer (single duvet cover only at £73.50). Navy is by far the most loved. Which way are you leaning?'
        },
        {
          match: /care|wash|iron/i,
          reply: 'Long-staple cotton, machine-washable at 40 degrees, tumble dry low. Iron damp for the crispest finish. The scalloped edge is hand-finished, so always launder inside out and avoid hot temperatures.'
        },
        {
          match: /ship|deliver|warehouse|us|america|international/i,
          reply: 'We ship the bed linen ourselves from our UK warehouse, usually next working day if you order before lunch. UK delivery only at the moment, the US is on the roadmap but not yet live. We do not ship internationally beyond that.'
        },
        {
          match: /portmeirion|tableware|plate/i,
          reply: 'The Sophie Conran for Portmeirion range is sold here on the site too, with its own page. Beautiful alongside the bed linen as a coherent home story. The link is in the cross-sell at the bottom of this page.'
        }
      ],
      fallback: 'Anything else I can help with on bed linen? Sizes, colours, care, delivery or other ways the brand shows up around the house.'
    },

    'john-lewis': {
      opener: 'Welcome. The Cocobella is Sophie\'s sofa exclusive to John Lewis, and there is also a separate Felix at John Lewis. Happy to talk through how the Cocobella differs from the direct Coco, or which fabric works in your room.',
      rules: [
        {
          match: /coco.*cocobella|cocobella.*coco|differ|different|same|compare/i,
          reply: 'Both made in the UK, both with solid birch frames, both in our striped fabrics. The differences: Coco has individual coil springs, all-natural feather and down fills, box-edge or knife-edge cushions, full bespoke. Cocobella has fishmouth sprung steel, recycled foam and feather, knife edge only, standard sizes. The Cocobella is sold exclusively through John Lewis with 0% finance and old-sofa disposal included. The comparison table on the page covers it in detail.'
        },
        {
          match: /finance|payment|0%/i,
          reply: 'The 0% finance is offered by John Lewis on the Cocobella. Up to 24 months interest-free on orders over a certain value (John Lewis sets the threshold). It is the route I would point a price-sensitive customer to first, alongside their store delivery and disposal service.'
        },
        {
          match: /store|see|sit|try|nearest/i,
          reply: 'Cocobella is on display in twelve John Lewis stores across the UK. Sit on it, feel the fabric, take a swatch home. The store finder on this page links straight to John Lewis\'s store list. Which town are you closest to?'
        },
        {
          match: /felix|second.*range|other.*range|fixed.*back/i,
          reply: 'Felix is the second route through John Lewis. A separate version of the Felix sofa, made for and sold through John Lewis. Useful if you love the brand but the price point on the own range does not quite fit. The link sits below the comparison table on this page.'
        },
        {
          match: /salthrop/i,
          reply: 'Salthrop is one of the eight Sophie Conran ranges, but it is sold direct from us, not through John Lewis. UK only at the moment, 119cm deep, fishmouth steel, loose cover with a dressmaker skirt and a curated fabric range. Worth a look if you want something deeper than the Coco.'
        }
      ],
      fallback: 'Anything else? The Cocobella, the Felix at John Lewis, the comparison with the direct Coco, or the John Lewis purchase route.'
    },

    'portmeirion': {
      opener: 'Welcome. The Sophie Conran for Portmeirion range is sold here on this site, twenty years on from the first white porcelain pieces. I can talk you through the six collections, the glaze, where it is made, or how shipping works.',
      rules: [
        {
          match: /glaze|mark|pencil|grey|black/i,
          reply: 'The grey or black markings are from the cutlery, not the glaze. Our glaze is harder than many cutlery metals (especially softer stainless steels), so the cutlery deposits a tiny amount of metal on the surface that looks like a pencil mark. Bar Keepers Friend, available in supermarkets, removes it cleanly. Sophie Conran for Arthur Price cutlery is high-grade enough not to mark the glaze.'
        },
        {
          match: /china|made|where|country|factory/i,
          reply: 'Designed in the UK with Portmeirion\'s testing lab in Stoke-on-Trent. The porcelain is manufactured in China to a quality and consistency we have not found elsewhere, working with manufacturers who meet Portmeirion\'s industry-leading standards. The accompanying glassware is hand-blown in Turkey by master glassblowers. We treat all of that as a feature, not something to apologise for.'
        },
        {
          match: /ship|deliver|fulfil|warehouse/i,
          reply: 'Your order is placed with us, then packed and dispatched by Portmeirion themselves: UK customers ship from the UK warehouse, US customers ship from the US warehouse. UK delivery is typically two to four working days. US delivery is typically four to seven. We do not currently ship internationally, but Portmeirion direct does, and John Lewis stocks a curated selection in their stores and online too.'
        },
        {
          match: /buy|shop|order|basket/i,
          reply: 'Just here. Each collection has its own page with the full range, prices and add-to-basket. The order goes to Portmeirion to pack and dispatch direct to you. If you would rather buy through John Lewis, that route is open too, they carry a curated selection of the most-loved pieces.'
        },
        {
          match: /lid|replace|broken/i,
          reply: 'Replacement lids are not available at the moment, the pieces are made and sold as full sets only. Portmeirion\'s customer service team on 01782 743427 is the right next step if you have a specific need.'
        },
        {
          match: /collection|range|colour|colour|pattern/i,
          reply: 'Six collections live now: White Porcelain (the original 2006 range), Floret (painted floral motif in Grey, Robin\'s Egg, Cream), Arbor (trellis-inspired), Honey Pot Mugs (five colourways), Celadon (soft sage-green) and Lavandula (lavender-toned, our newest). Which mood are you after, or want me to suggest based on what you already have at home?'
        }
      ],
      fallback: 'Anything else I can help with? The six collections, the glaze, how shipping works, or the partnership story.'
    },

    'trade': {
      opener: 'Hello, Helen. Need spec sheets for a client presentation, or can I help you build a quote? I can pull tear sheets and fabric photography in any format you need.',
      rules: [
        {
          match: /spec|tear|sheet|presentation|client/i,
          reply: 'Of course. Tell me the product (Coco, Barton, Henrietta, Isobel, Felix, Sophia) and any fabric specifics, and I will pull a tear sheet, the dimensioned drawing, and the fabric photograph. PDF or PowerPoint, your choice. Want it sent to your studio email?'
        },
        {
          match: /quote|build|price|trade/i,
          reply: 'Quote builder is in your top nav. Add products and configurations, set the client name, and either save it as a working draft or send it directly. If you upload your studio logo to your account, the cover page picks it up. Need a hand with anything specific?'
        },
        {
          match: /lead|delivery|when/i,
          reply: 'Trade lead times are the same as consumer, around 8 weeks UK. We can prioritise on contract orders if a project deadline needs it. Stock-base programme means three to six weeks if a Cool Coconut, Snow Drop or popular stripe matches. Tell me the project and the deadline and I can sense-check.'
        },
        {
          match: /com|customer\'s own/i,
          reply: 'COM is welcome on most ranges. Coco, Barton, Isobel, Henrietta, all yes. Sophia is not COM. Cocobella is the John Lewis exclusive range and not available through us, and Felix has a separate version sold through John Lewis (the SC own Felix is COM-friendly). Salthrop is our own range, UK only, but with a curated fabric list rather than open COM. Send the fabric specifications (composition, weight, repeat, fire test certificate if you have one) to Adam and we will price it up.'
        }
      ],
      fallback: 'Helen, anything else? Spec sheets, quote builder, lead times, COM, lookbooks. I can pull whatever you need for the project.'
    }
  };

  /* ------------------- NADIA CONTROLLER ------------------- */
  var Nadia = {
    fab: null,
    bubble: null,
    messagesEl: null,
    typingEl: null,
    inputEl: null,
    sendBtn: null,
    closeBtn: null,
    script: null,
    isOpen: false,
    opened: false,
    appearTimer: null,

    init: function(context) {
      this.script = scripts[context] || scripts['home'];
      this.fab = document.getElementById('sc-nadia-fab');
      this.bubble = document.getElementById('sc-nadia-bubble');
      this.messagesEl = document.getElementById('sc-nadia-messages');
      this.typingEl = document.getElementById('sc-nadia-typing');
      this.inputEl = document.getElementById('sc-nadia-input');
      this.sendBtn = document.getElementById('sc-nadia-send');
      this.closeBtn = document.getElementById('sc-nadia-close');
      if (!this.fab) return;

      this.bindEvents();
      this.scheduleAppearance(context);
    },

    scheduleAppearance: function(context) {
      // Production brief specified a 10-second delay on home. For the prototype demo
      // we use much shorter delays so reviewers can see the chat icon without waiting.
      var delay = (context === 'in-stock') ? 1500 :
                  (context === 'home') ? 2500 :
                  2000;
      // If returning visitor (sessionStorage flag), appear immediately
      try {
        if (sessionStorage.getItem('sc-nadia-seen')) delay = 300;
        sessionStorage.setItem('sc-nadia-seen', '1');
      } catch (e) {}

      var self = this;
      this.appearTimer = setTimeout(function() {
        self.fab.classList.add('is-visible');
      }, delay);
    },

    bindEvents: function() {
      var self = this;

      this.fab.addEventListener('click', function() { self.open(); });

      // Any element with data-sc-nadia-open opens the chat
      document.addEventListener('click', function(e) {
        var target = e.target.closest('[data-sc-nadia-open]');
        if (target) {
          e.preventDefault();
          self.open();
        }
      });

      this.closeBtn.addEventListener('click', function() { self.close(); });

      this.sendBtn.addEventListener('click', function() { self.handleSend(); });

      this.inputEl.addEventListener('keydown', function(e) {
        if (e.key === 'Enter') { e.preventDefault(); self.handleSend(); }
      });
    },

    open: function() {
      if (this.appearTimer) { clearTimeout(this.appearTimer); }
      this.fab.classList.remove('is-visible');
      this.bubble.classList.add('is-visible');
      this.isOpen = true;

      if (!this.opened) {
        this.opened = true;
        // On home: lead with catalogue offer, but only if not yet requested
        var isHome = this.context === 'home';
        var alreadyRequested = (window.SC && SC.catalogue && SC.catalogue.isAlreadySubmitted && SC.catalogue.isAlreadySubmitted());
        if (isHome && !alreadyRequested) {
          this.appendMessage('nadia', 'Hello, I am Nadia. Before we dive in, have you ordered our catalogue yet?');
          this.appendChoice([
            { label: 'Order it now',  action: 'catalogue' },
            { label: 'Just browsing', action: 'd2c' }
          ]);
        } else {
          this.appendMessage('nadia', this.script.opener);
        }
      }

      // Focus the input after a short delay
      var self = this;
      setTimeout(function() { self.inputEl.focus(); }, 200);
    },

    close: function() {
      this.bubble.classList.remove('is-visible');
      this.fab.classList.add('is-visible');
      this.isOpen = false;
    },

    handleSend: function() {
      var text = this.inputEl.value.trim();
      if (!text) return;
      this.appendMessage('customer', text);
      this.inputEl.value = '';

      var reply = this.matchReply(text);
      this.respondAfterDelay(reply);
    },

    matchReply: function(text) {
      var rules = this.script.rules || [];
      for (var i = 0; i < rules.length; i++) {
        var rule = rules[i];
        var match = (rule.match instanceof RegExp) ? rule.match.test(text) :
                    (typeof rule.match === 'function') ? rule.match(text) : false;
        if (match) {
          return (typeof rule.reply === 'function') ? rule.reply(text) : rule.reply;
        }
      }
      return this.script.fallback || 'Tell me a bit more about what you are looking for.';
    },

    respondAfterDelay: function(text) {
      var self = this;
      // Show typing indicator
      this.typingEl.classList.add('is-visible');
      this.scrollToBottom();
      // Variable delay based on response length, but capped
      var delay = Math.min(800 + (text.length * 8), 1800);
      setTimeout(function() {
        self.typingEl.classList.remove('is-visible');
        self.appendMessage('nadia', text);
      }, delay);
    },

    appendMessage: function(from, text) {
      var div = document.createElement('div');
      div.className = 'sc-nadia-msg from-' + from;
      div.textContent = text;
      this.messagesEl.appendChild(div);
      this.scrollToBottom();
    },

    /* Render a row of inline-choice buttons under Nadia's last message.
       items: [{ label, action }] where action is 'catalogue' or 'd2c'. */
    appendChoice: function(items) {
      var wrap = document.createElement('div');
      wrap.className = 'sc-nadia-choice';
      var self = this;
      items.forEach(function(it) {
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'sc-nadia-choice-btn';
        btn.textContent = it.label;
        btn.addEventListener('click', function() {
          // disable to prevent double-tap
          wrap.querySelectorAll('button').forEach(function(b) { b.disabled = true; });
          self.appendMessage('user', it.label);
          if (it.action === 'catalogue') {
            // Close the FAB and open the inline catalogue panel
            self.close();
            if (window.SC && SC.catalogue && SC.catalogue.open) SC.catalogue.open();
          } else {
            // Continue D2C in the FAB
            setTimeout(function() {
              self.respondAfterDelay(self.script.opener);
            }, 200);
          }
        });
        wrap.appendChild(btn);
      });
      this.messagesEl.appendChild(wrap);
      this.scrollToBottom();
    },

    scrollToBottom: function() {
      var self = this;
      requestAnimationFrame(function() {
        self.messagesEl.scrollTop = self.messagesEl.scrollHeight;
      });
    }
  };

  /* ------------------- DRAWER ------------------- */
  function bindDrawer() {
    var drawer = document.getElementById('sc-drawer');
    if (!drawer) return;
    document.addEventListener('click', function(e) {
      if (e.target.closest('[data-sc-drawer-open]')) {
        drawer.classList.add('is-open');
        drawer.setAttribute('aria-hidden', 'false');
      }
      if (e.target.closest('[data-sc-drawer-close]')) {
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      }
    });
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape') {
        drawer.classList.remove('is-open');
      }
    });
  }

  /* ------------------- ACCORDION ------------------- */
  function bindAccordion() {
    document.addEventListener('click', function(e) {
      var q = e.target.closest('.sc-qa-q');
      if (q) {
        var item = q.closest('.sc-qa-item');
        if (item) item.classList.toggle('is-open');
      }
    });
  }

  /* ------------------- CONFIG SELECTORS ------------------- */
  function bindConfig() {
    document.addEventListener('click', function(e) {
      var chip = e.target.closest('.sc-config-chip');
      if (chip) {
        var group = chip.closest('.sc-config-options');
        if (!group) return;
        var siblings = group.querySelectorAll('.sc-config-chip');
        siblings.forEach(function(s) { s.classList.remove('is-selected'); });
        chip.classList.add('is-selected');
      }
    });
  }

  /* ------------------- FILTER BAR ------------------- */
  function bindFilters() {
    document.addEventListener('click', function(e) {
      var chip = e.target.closest('.sc-filter-chip');
      if (chip) {
        chip.classList.toggle('is-active');
      }
    });
  }

  /* ------------------- BOOT ------------------- */
  document.addEventListener('DOMContentLoaded', function() {
    bindDrawer();
    bindAccordion();
    bindConfig();
    bindFilters();

    // Determine page context for Nadia
    var context = document.body.getAttribute('data-sc-page') || 'home';
    Nadia.init(context);
  });

  // Expose for debugging and cross-module use
  window.SC = window.SC || {};
  window.SC.Nadia = Nadia;
  // Expose the scripts dictionary too so other modules (e.g. the
  // product configurator's "talk this through" modal) can resolve a
  // specific context script without duplicating the rules.
  Nadia._scripts = scripts;
})();
